import { useCallback, useEffect, useRef } from 'react';
import { useParams } from '@tanstack/react-router';

import { useConversation, useSendMessage } from '@/api/generated';
import { Chat } from '@/components/blocks/chat/chat';
import { ChatUser } from '@/components/ui/chat-avatar';
import type { IChatContext } from '@/hooks/use-chat';

interface ManagedChatProps {
  onOpenFeedback: (messageId: string) => void;
}
const ManagedChat = ({ onOpenFeedback }: ManagedChatProps) => {
  const { threadId } = useParams({ from: '/app/threads/$threadId' });
  const chatRef = useRef<IChatContext>(null);

  const { data, refetch } = useConversation(threadId);
  const { mutate: sendMessage } = useSendMessage();

  useEffect(() => {
    chatRef.current?.clearMessages();
    data?.messages.forEach((message) => {
      message.content.forEach((content) => {
        chatRef.current?.postMessage({
          id: message.id,
          avatar: {
            user: message.role === 'user' ? ChatUser.USER : ChatUser.BOT,
            size: 'md',
          },
          content: content.text,
          timestamp: new Date(message.createdAt as string),
        });
      });
    });
  }, [data?.messages]);

  const handleSendMessage = useCallback(
    (message: string) => {
      sendMessage(
        {
          threadId,
          data: {
            content: {
              text: message,
              type: 'text',
            },

            role: 'user',
          },
        },
        {
          onSettled: () => {
            refetch();
          },
        }
      );
    },
    [refetch, sendMessage, threadId]
  );

  return (
    <Chat
      ref={chatRef}
      status={'online'}
      onSendMessage={handleSendMessage}
      onOpenFeedback={onOpenFeedback}
    />
  );
};

export default ManagedChat;
